import { FileDropZone } from '../ui/FileDropZone';
import { MAX_VAULT_DOCUMENT_BYTES } from '@accounting/shared';
import type { UploadItem, UploadPhase } from '../../hooks/useVault';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const PHASE_LABEL: Record<UploadPhase, string> = {
  queued: 'Waiting',
  uploading: 'Uploading',
  reading: 'Reading with AI',
  done: 'Filed',
  error: 'Failed',
};

/** 0-100 for the little inline bar. Reading is the long half of the job. */
const PHASE_PROGRESS: Record<UploadPhase, number> = {
  queued: 4,
  uploading: 35,
  reading: 75,
  done: 100,
  error: 100,
};

/**
 * Drop a stack of files. One row per file with its own progress, so a 40-file
 * drop reads as forty small jobs rather than one opaque spinner.
 */
export function VaultUploadPanel({
  items,
  busy,
  disabled,
  onFiles,
  onClearFinished,
}: {
  items: UploadItem[];
  busy: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  onClearFinished: () => void;
}) {
  const finished = items.filter((i) => i.phase === 'done' || i.phase === 'error').length;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <FileDropZone
        multiple
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.heic,.txt,.csv,.xls,.xlsx,.doc,.docx"
        busy={busy}
        disabled={disabled}
        onFiles={onFiles}
        hint={`Drop as many files as you like — PDFs, scans, photos, spreadsheets. Up to ${
          MAX_VAULT_DOCUMENT_BYTES / 1024 / 1024
        } MB each. Each one is stored first, then read for its date.`}
      />

      {items.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <strong style={{ fontSize: 13 }}>
              {busy ? `Filing ${items.length - finished} of ${items.length}…` : `${items.length} file${items.length === 1 ? '' : 's'}`}
            </strong>
            {finished > 0 && !busy && (
              <button className="btn-secondary" onClick={onClearFinished}>
                Clear
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((item) => (
              <UploadRow key={item.key} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  const failed = item.phase === 'error';
  const warned = item.phase === 'done' && !!item.warning;
  const barColor = failed ? 'var(--danger)' : warned ? 'var(--tag-warn-fg)' : 'var(--accent)';

  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.fileName}
        </span>
        <span className="muted" style={{ whiteSpace: 'nowrap' }}>
          {formatBytes(item.sizeBytes)} · {failed ? 'Failed' : warned ? 'Stored — needs review' : PHASE_LABEL[item.phase]}
        </span>
      </div>
      <div
        style={{
          height: 4,
          borderRadius: 2,
          background: 'var(--border)',
          overflow: 'hidden',
          marginTop: 3,
        }}
      >
        <div
          style={{
            width: `${PHASE_PROGRESS[item.phase]}%`,
            height: '100%',
            background: barColor,
            transition: 'width 0.25s ease',
          }}
        />
      </div>
      {(item.error || item.warning) && (
        <div className={failed ? 'field-error' : 'muted'} style={{ fontSize: 11, marginTop: 2 }}>
          {item.error ?? item.warning}
        </div>
      )}
    </div>
  );
}
